import {
  Children,
  Fragment,
  cloneElement,
  isValidElement,
  useEffect,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { ControlOutlined } from '@ant-design/icons';

/*
 * ใช้ร่วมกันสองผัง — ผังหน่วยงาน (OrgFlow) และผังผู้บริหารตามตำแหน่ง (OrgPositions)
 * ผู้ใช้ขอให้ผังผู้บริหารมีเครื่องมือแบบเดียวกัน (19 ก.ย. 2569) จึงย้ายออกมาจาก OrgFlow.tsx
 * ⚠ ห้ามทำสำเนาไว้สองที่ — หน้าตา/พฤติกรรมของสองผังต้องตรงกันเสมอ
 */

/* ================================================================
   ปุ่มเครื่องมือที่คลี่ออกเป็นส่วนโค้ง
   ================================================================ */

type FanChild = ReactElement<{ style?: CSSProperties; tabIndex?: number }>;

/**
 * แบนรายการปุ่มให้เป็นชุดเดียว รวมถึงปุ่มที่ห่อมาใน <> </>
 *
 * ⚠ Children.toArray ไม่เดินเข้าไปใน Fragment — มันนับทั้งก้อนเป็นหนึ่งลูก
 * ผู้เรียกส่ง toolsExtra มาเป็น Fragment ที่มีสามปุ่มข้างใน ถ้าไม่แบนตรงนี้
 * ส่วนโค้งจะเห็นเป็นสี่ใบ แล้ววางปุ่มสามใบสุดท้ายซ้อนกันอยู่จุดเดียว
 */
function fanItems(nodes: ReactNode): FanChild[] {
  return Children.toArray(nodes).flatMap((c) => {
    if (!isValidElement(c)) return [];
    if (c.type === Fragment) return fanItems((c.props as { children?: ReactNode }).children);
    return [c as FanChild];
  });
}

/**
 * ยุบปุ่มเครื่องมือทั้งชุดให้เหลือปุ่มเดียว แล้วคลี่ออกเป็นส่วนโค้งเมื่อชี้หรือกด
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องยุบ
 * ────────────────────────────────────────────────────────────────
 *
 * ปุ่มหกใบเรียงกันมุมขวาบนกินความกว้างเกือบ 280px ทับพื้นที่ผังตลอดเวลา
 * ทั้งที่ทั้งหกใบเป็นของที่กด "ครั้งเดียวจบ" — กางทั้งหมด สลับแนว เต็มจอ บันทึกรูป
 * ต่างจากชุดซูมมุมขวาล่างที่กดซ้ำ ๆ ระหว่างไล่ดู ซึ่งจึงไม่ได้ยุบตามมาด้วย
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมมีทั้ง "ชี้แล้วคลี่" และ "กดแล้วตรึง"
 * ────────────────────────────────────────────────────────────────
 *
 * ถ้าชี้แล้วคลี่อย่างเดียว มันจะหุบทันทีที่เมาส์ขยับออกนอกส่วนโค้ง
 * ซึ่งใช้ไม่ได้กับคนที่ต้องกดสองปุ่มติดกัน (กางทั้งหมด แล้วบันทึกรูป)
 * ถ้ากดอย่างเดียว คนที่แค่อยากรู้ว่ามีอะไรอยู่ในนั้นต้องกดก่อนถึงจะเห็น
 *
 * ⚠ ปุ่มลูกทุกใบอยู่ใน DOM ตลอด ไม่ได้สร้างใหม่ตอนคลี่
 *   ถ้าสร้างตอนเปิด transition จะไม่ทำงาน (ของที่เพิ่งเกิดไม่มีค่าเดิมให้ไล่จาก)
 *   และ ThemeToggle จะเสียสถานะภายในทุกครั้งที่หุบ
 */
export function ToolFan({ children }: { children: ReactNode }) {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hover || pinned;

  const items = fanItems(children);
  const n = items.length;

  /**
   * พิกัดบนส่วนโค้ง — กวาดจาก 180° (ซ้ายตรง) ลงมา 88° จึงจบที่เกือบตรงลง
   *
   * ⚠ รัศมีคำนวณจากจำนวนปุ่ม ไม่ได้ตรึงเป็นตัวเลข
   *   ระยะระหว่างปุ่มบนส่วนโค้งคือ R × มุมต่อช่วง ถ้าตรึง R ไว้แล้ววันหนึ่งเพิ่มปุ่ม
   *   ปุ่มจะค่อย ๆ ขยับมาซ้อนกันเองโดยไม่มีอะไรฟ้อง — 44px คือระยะที่ปุ่ม 38px เว้นช่องไฟพอดี
   */
  const SWEEP = 88;
  const stepDeg = n > 1 ? SWEEP / (n - 1) : 0;
  const stepRad = (stepDeg * Math.PI) / 180;
  const radius = n > 1 ? Math.max(120, 44 / stepRad) : 0;

  /* Escape ต้องหุบได้ ไม่งั้นคนที่ตรึงไว้แล้วต้องเล็งกดปุ่มเดิมซ้ำเพื่อปิด */
  useEffect(() => {
    if (!pinned) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPinned(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pinned]);

  return (
    <div
      className={`flow-fan${open ? ' is-open' : ''}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/*
        แผ่นรับเมาส์ใส ๆ คลุมพื้นที่ที่ปุ่มคลี่ไปอยู่

        ⚠ จำเป็น ไม่ใช่ของประดับ — ปุ่มลูกอยู่ห่างจากปุ่มแม่ร้อยกว่าพิกเซล
        ระหว่างทางเป็นที่ว่างซึ่งไม่ใช่ลูกของกล่องนี้ ถ้าไม่มีแผ่นนี้ เมาส์ที่ลากทแยง
        ไปหาปุ่มลูกจะหลุดออกนอกกล่องกลางทาง แล้วส่วนโค้งจะหุบใส่หน้าก่อนถึงปุ่ม
      */}
      {open && <span className="fan-shield" aria-hidden="true" />}

      {items.map((child, i) => {
        const a = ((180 - stepDeg * i) * Math.PI) / 180;
        const x = open ? Math.round(Math.cos(a) * radius) : 0;
        const y = open ? Math.round(Math.sin(a) * radius) : 0;
        return (
          <span
            key={child.key ?? i}
            className="fan-slot"
            style={{
              transform: `translate(${x}px, ${y}px) scale(${open ? 1 : 0.55})`,
              opacity: open ? 1 : 0,
              pointerEvents: open ? 'auto' : 'none',
              /* คลี่ไล่จากใบแรกออกไป · หุบไล่กลับจากใบท้ายเข้ามา */
              transitionDelay: `${(open ? i : n - 1 - i) * (open ? 34 : 20)}ms`,
            }}
          >
            {/* ตอนหุบต้องออกจากลำดับ Tab ด้วย ไม่ใช่แค่มองไม่เห็น
                ปุ่มที่โฟกัสได้แต่มองไม่เห็นคือกับดักของคนที่ไล่ด้วยแป้นพิมพ์ */}
            {cloneElement(child, { tabIndex: open ? 0 : -1 })}
          </span>
        );
      })}

      <button
        type="button"
        className="icobtn fan-key"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={open ? 'ปิดเครื่องมือผัง' : 'เปิดเครื่องมือผัง'}
        data-tip={open ? 'ปิดเครื่องมือ' : 'เครื่องมือผัง'}
        onClick={() => setPinned((v) => !v)}
      >
        <ControlOutlined aria-hidden="true" />
      </button>
    </div>
  );
}

